
import { defineComponent, PropType, ref } from 'vue';
import { requiredIf } from '@vuelidate/validators';
import {
	UiForm,
	UiCardDivider,
	UiRadioTabs,
	UiButton,
	UiInput,
	UiCheckbox,
	mask,
	useToggle,
	useForm
} from '@/ui-kit';
import { BusinessDay } from '../types';
import { useDays } from '../composition';

enum OpenState {
	open,
	closed,
	custom
}

const component = defineComponent({
	name: 'edit-phone-number-business-hours-dialog',
	components: {
		UiCardDivider,
		UiRadioTabs,
		UiButton,
		UiInput,
		UiCheckbox,
		UiForm
	},
	directives: { mask },
	emits: ['update:modelValue'],
	props: {
		modelValue: {
			type: Array as PropType<Array<BusinessDay>>,
			default: () => []
		}
	},
	setup(props, { emit }) {
		const {
			formatTime,
			getAmPmTime,
			AmPm,
			getTime,
			...restDays
		} = useDays();
		const { isOpened, toggleOpened } = useToggle();
		const { formData, resetForm, ...restFormData } =
			useForm(
				{
					openState: OpenState.open,
					from: '08:00am',
					to: '08:00pm',
					selectedDays: [] as any
				},
				{
					from: {
						required: requiredIf(
							(): boolean =>
								isOpened.value &&
								formData.openState ===
									OpenState.custom
						)
					},
					to: {
						required: requiredIf(
							(): boolean =>
								isOpened.value &&
								formData.openState ===
									OpenState.custom
						),
						toMoreThanFrom: (): boolean => {
							return (
								!isOpened.value ||
								formData.openState !==
									OpenState.custom ||
								formatTime(formData.from) <
									formatTime(formData.to)
							);
						}
					}
				}
			);

		const selectedDays = ref<number[]>([]);
		const applyToOtherDays = ref(false);

		const closeDialog = () => toggleOpened(false);

		const timeMask = (value: string) => [
			/[0-1]/,
			value[0] === '1' ? /[0-2]/ : /[1-9]/,
			':',
			/[0-5]/,
			/[0-9]/
		];

		const isSelected = (id: number) =>
			selectedDays.value.includes(id);

		const toggleDay = (id: number) => {
			if (isSelected(id)) {
				selectedDays.value =
					selectedDays.value.filter(
						(selectedId) => selectedId !== id
					);
			} else {
				selectedDays.value = [
					...selectedDays.value,
					id
				];
			}
		};

		const openDialog = (id: number) => {
			resetForm();
			selectedDays.value = [id];
			applyToOtherDays.value = false;
			const selectedDay = props.modelValue.find(
				(day) => day.id === id
			);

			if (selectedDay) {
				formData.openState =
					selectedDay.closedAllDay
						? OpenState.closed
						: selectedDay.openAllDay
						? OpenState.open
						: OpenState.custom;

				formData.from = getAmPmTime(
					selectedDay.from
				);
				formData.to = getAmPmTime(selectedDay.to);
			}

			toggleOpened(true);
		};

		const getItemTime = (time: string) =>
			getTime(time).join(':');

		const updateItem = (
			item: BusinessDay
		): BusinessDay => {
			return {
				...item,
				closedAllDay: Number(
					formData.openState === OpenState.closed
				),
				openAllDay: Number(
					formData.openState === OpenState.open
				),
				from:
					formData.openState === OpenState.custom
						? getItemTime(formData.from)
						: null,
				to:
					formData.openState === OpenState.custom
						? getItemTime(formData.to)
						: null
			};
		};

		const submit = () => {
			const data = props.modelValue.map((item) =>
				isSelected(item.id as number)
					? updateItem(item)
					: item
			);
			emit('update:modelValue', data);
			closeDialog();
		};

		return {
			isOpened,
			closeDialog,
			OpenState,
			applyToOtherDays,
			toggleDay,
			timeMask,
			formData,
			openDialog,
			selectedDays,
			isSelected,
			submit,
			resetForm,
			AmPm,
			...restFormData,
			...restDays
		};
	}
});

export default component;
